import { useHandler } from "@redotech/react-util/hook";
import classNames from "classnames";
import { JSXElementConstructor, memo, ReactNode, useState } from "react";
import CheckIcon from "../../arbiter-icon/check.svg";
import ChevronRightIcon from "../../arbiter-icon/chevron-right.svg";
import { Dropdown } from "../../dropdown";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { SpacingValue } from "../../theme/box";
import {
  TextColorValue,
  TextSizeValue,
  TextWeightValue,
} from "../../theme/typography";
import { OverflowTooltip } from "../../tooltip/overflow-tooltip";
import { RedoBadge, RedoBadgeProps, RedoBadgeSize } from "../badge/redo-badge";
import { RedoCheckbox } from "../checkbox/redo-checkbox";
import { RedoCommandMenuItem } from "../command-menu/redo-command-menu";
import {
  RedoCommandShortcut,
  RedoCommandShortcutSize,
} from "../command-menu/redo-command-shortcut";
import {
  ParentRedoListItem as ParentRedoListItemType,
  RedoList,
  RedoListItem,
  RedoListItemSelectedSource,
} from "./redo-list";
import { RedoListItemCommandMenu } from "./redo-list-item-command-menu";
import { LeadingItem, RedoListItemLeadingItem } from "./redo-list-item-lead";
import * as redoListItemCss from "./redo-list-item.module.css";

export const redoListItemSelectionVariant = [
  "checkmark",
  "checkbox",
  "checkbox-reverse",
  "none",
] as const;
export type RedoListItemSelectionVariant =
  (typeof redoListItemSelectionVariant)[number];

export const redoListItemSize = ["xs", "sm", "md", "lg"] as const;
export type RedoListItemSize = (typeof redoListItemSize)[number];

interface BaseListItemProps {
  focused?: boolean;
  selected?: boolean;
  indeterminate?: boolean;
  menu?: RedoCommandMenuItem[] | { onClick: () => void };
  hotkey?: string[] | string;
  selectionVariant?: RedoListItemSelectionVariant;
  disabled?: boolean;
  size?: RedoListItemSize;
  card?: boolean;
  useWrapper?: boolean;
  infoBadge?: Omit<RedoBadgeProps, "size">;
  TrailingIcon?: JSXElementConstructor<any>;
  onItemHovered?(hovered: boolean): void;
  onItemSelected?(selected: boolean): void;
  dataTestId?: string;
}

export interface TextRedoListItemProps extends BaseListItemProps {
  leadingItem?: LeadingItem;
  text: string;
  supportingText?: string;
  placeholder?: boolean;
  hideOnHoverTooltip?: boolean;
}

export const TextRedoListItem = memo(function TextRedoListItem({
  leadingItem,
  text,
  supportingText,
  size = "sm",
  disabled = false,
  selected = false,
  useWrapper = true,
  placeholder = false,
  hideOnHoverTooltip = false,
  ...rest
}: TextRedoListItemProps) {
  const textColor: TextColorValue =
    ((disabled || placeholder) && "disabled") || "primary";
  const textWeight: TextWeightValue =
    ((selected || size === "sm") && !placeholder && "medium") || "regular";

  const supportingTextColor: TextColorValue =
    ((disabled || placeholder) && "disabled") || "tertiary";

  const [ref, setRef] = useState<HTMLDivElement | null>(null);

  const contents = (
    <>
      {leadingItem && (
        <RedoListItemLeadingItem leadingItem={leadingItem} size={size} />
      )}
      <Flex dir="column" gap="none" justify="center" overflow="hidden">
        <OverflowTooltip
          hideOnHover={hideOnHoverTooltip}
          overflowRef={ref}
          tooltipProps={{ title: text }}
          wrapperStyle={{ overflow: "hidden" }}
        >
          <Text
            className={redoListItemCss.text}
            fontSize={itemSizeToTextSize[size]}
            fontWeight={textWeight}
            overflow="hidden"
            ref={setRef}
            textColor={textColor}
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {text.trim() || <br />}
          </Text>
        </OverflowTooltip>
        {supportingText && (
          <Text
            fontSize={itemSizeToSupportingTextSize[size]}
            fontWeight="regular"
            textColor={supportingTextColor}
          >
            {supportingText}
          </Text>
        )}
      </Flex>
    </>
  );

  if (!useWrapper) {
    return contents;
  }

  return (
    <RedoListItemContainer
      disabled={disabled}
      selected={selected}
      size={size}
      {...rest}
    >
      {contents}
    </RedoListItemContainer>
  );
});

export interface BadgeRedoListItemProps extends BaseListItemProps {
  badge: Omit<RedoBadgeProps, "size">;
}

export const BadgeRedoListItem = memo(function BadgeRedoListItem({
  badge,
  size = "sm",
  useWrapper = true,
  ...rest
}: BadgeRedoListItemProps) {
  const [overflowRef, setOverflowRef] = useState<HTMLElement | null>(null);
  if (!useWrapper) {
    return (
      <OverflowTooltip
        overflowRef={overflowRef}
        tooltipProps={{ placement: "top", title: badge.text }}
      >
        <RedoBadge
          {...badge}
          setRef={setOverflowRef}
          size={listItemSizeToBadgeSize[size]}
        />
      </OverflowTooltip>
    );
  }

  return (
    <RedoListItemContainer size={size} {...rest}>
      <OverflowTooltip
        overflowRef={overflowRef}
        tooltipProps={{ placement: "top", title: badge.text }}
      >
        <RedoBadge
          {...badge}
          setRef={setOverflowRef}
          size={listItemSizeToBadgeSize[size]}
        />
      </OverflowTooltip>
    </RedoListItemContainer>
  );
});

const listItemSizeToBadgeSize: Record<RedoListItemSize, RedoBadgeSize> = {
  xs: "xs",
  sm: "xs",
  md: "sm",
  lg: "md",
};

export const RedoListItemContainer = memo(function RedoListItemContainer({
  focused = false,
  selected = false,
  indeterminate = false,
  menu,
  hotkey,
  selectionVariant = "checkmark",
  disabled = false,
  size = "sm",
  card,
  onItemHovered,
  onItemSelected,
  infoBadge,
  TrailingIcon,
  children,
  dataTestId,
}: BaseListItemProps & { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useHandler(() => {
    setIsHovered(true);
    onItemHovered?.(true);
  });

  const handleMouseLeave = useHandler(() => {
    setIsHovered(false);
    onItemHovered?.(false);
  });

  const itemInFocus = isHovered || focused || menuOpen;

  const showMenu =
    itemInFocus && menu && (!Array.isArray(menu) || menu.length > 0);

  return (
    <Flex
      align="center"
      className={classNames(
        redoListItemCss.container,
        card && redoListItemCss.card,
        disabled && redoListItemCss.disabled,
        focused && redoListItemCss.focused,
        selected && redoListItemCss.selected,
      )}
      data-testid={dataTestId}
      grow={1}
      justify="space-between"
      onClick={(e) => {
        if (!onItemSelected) {
          return;
        }
        if (disabled) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (indeterminate) {
          onItemSelected(false);
        } else {
          onItemSelected(!selected);
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      overflow="hidden"
      {...itemSizeToContainerPadding[size]}
    >
      <Flex align="center" overflow="hidden" w="full">
        {selectionVariant === "checkbox" && (
          <RedoCheckbox
            disabled={disabled}
            setValue={() => {
              if (indeterminate) {
                onItemSelected?.(false);
              } else {
                onItemSelected?.(!selected);
              }
            }}
            size="sm"
            value={indeterminate ? "indeterminate" : selected}
          />
        )}
        {children}
      </Flex>
      <Flex>
        {showMenu && (
          <RedoListItemCommandMenu
            disabled={disabled}
            menu={menu}
            open={menuOpen}
            setOpen={setMenuOpen}
          />
        )}
        {hotkey && !showMenu && (
          <RedoCommandShortcut
            hotkey={hotkey}
            size={itemSizeToShortcutSize[size]}
          />
        )}
        {infoBadge && (
          <RedoBadge {...infoBadge} size={listItemSizeToBadgeSize[size]} />
        )}
        {TrailingIcon && (
          <TrailingIcon
            className={classNames(
              redoListItemCss.icon,
              itemSizeToSizeClass[size],
            )}
          />
        )}
        {selectionVariant === "checkmark" && selected && !showMenu && (
          <CheckIcon
            className={classNames(
              redoListItemCss.checkmark,
              itemSizeToSizeClass[size],
            )}
          />
        )}
        {selectionVariant === "checkbox-reverse" && (
          <RedoCheckbox
            disabled={disabled}
            setValue={() => {
              if (indeterminate) {
                onItemSelected?.(false);
              } else {
                onItemSelected?.(!selected);
              }
            }}
            size="sm"
            value={indeterminate ? "indeterminate" : selected}
          />
        )}
      </Flex>
    </Flex>
  );
});

export const ParentRedoListItem = memo(function ParentRedoListItem<T>({
  size,
  itemSelected,
  selectionVariant,
  card,
  focused,
  disabledItems,
  selectedItems,
  indeterminateItems,
  item,
}: {
  size: RedoListItemSize;
  itemSelected?(args: {
    item: RedoListItem<T>;
    source: RedoListItemSelectedSource;
    index: number;
    selected: boolean;
  }): void;
  selectionVariant: RedoListItemSelectionVariant;
  card: boolean;
  focused: boolean;
  selectedItems?: (string | number | symbol)[] | (string | number | symbol);
  disabledItems?: (string | number | symbol)[] | (string | number | symbol);
  indeterminateItems?:
    | (string | number | symbol)[]
    | (string | number | symbol);
  item: ParentRedoListItemType<T>;
}) {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLDivElement | null>(
    null,
  );
  const [submenuFocusIndex, setSubmenuFocusIndex] = useState<
    number | undefined
  >(undefined);

  const handleItemClick = (selected: boolean) => {
    setIsSubmenuOpen(!isSubmenuOpen);
    item.onClickSideEffect?.();
  };

  const handleSubmenuItemSelected = useHandler(
    (args: {
      item: RedoListItem<T>;
      source: RedoListItemSelectedSource;
      index: number;
      selected: boolean;
    }) => {
      // When an item in the submenu is selected, delegate to parent handler
      itemSelected?.(args);
      setIsSubmenuOpen(false);
    },
  );

  return (
    <RedoListItemContainer
      focused={focused}
      onItemSelected={handleItemClick}
      size={size}
    >
      <Flex
        align="center"
        className={classNames(
          focused && redoListItemCss.focused,
          card && redoListItemCss.card,
        )}
        dir="row"
        justify="space-between"
        ref={setSubmenuAnchor}
        w="full"
      >
        <Flex align="center" dir="row" gap="sm">
          {item.leadingItem && (
            <RedoListItemLeadingItem
              leadingItem={item.leadingItem}
              size={item.leadingItemSize ?? size}
            />
          )}
          <Flex dir="column" gap="none">
            <Text
              fontSize={itemSizeToTextSize[size]}
              fontWeight={size === "sm" ? "medium" : "regular"}
              textColor="primary"
            >
              {item.text}
            </Text>
          </Flex>
        </Flex>

        <ChevronRightIcon color="tertiary" height={16} width={16} />
      </Flex>

      <Dropdown
        anchor={submenuAnchor}
        constrainHeight
        fitToAnchor={item.fitToAnchor ?? false}
        flexProps={{ p: "none" }}
        open={isSubmenuOpen}
        placement={item.subMenuPlacement}
      >
        <Flex px="lg" py="md">
          <RedoList
            card={card}
            disabledItems={disabledItems}
            emptyListMessage={item.emptyListMessage}
            focusedIndex={submenuFocusIndex}
            indeterminateItems={indeterminateItems}
            items={item.children}
            itemSelected={handleSubmenuItemSelected}
            refToListenTo={submenuAnchor}
            selectedItems={selectedItems}
            selectionVariant={selectionVariant}
            setFocusedIndex={setSubmenuFocusIndex}
            size={size}
          />
        </Flex>
      </Dropdown>
    </RedoListItemContainer>
  );
});

export const itemSizeToSizeClass: Record<RedoListItemSize, string> = {
  xs: redoListItemCss.xs,
  sm: redoListItemCss.sm,
  md: redoListItemCss.md,
  lg: redoListItemCss.lg,
};

export const itemSizeToShortcutSize: Record<
  RedoListItemSize,
  RedoCommandShortcutSize
> = { xs: "sm", sm: "sm", md: "md", lg: "lg" };

export const itemSizeToTextSize: Record<RedoListItemSize, TextSizeValue> = {
  xs: "xs",
  sm: "xs",
  md: "md",
  lg: "md",
};

export const itemSizeToSupportingTextSize: Record<
  RedoListItemSize,
  TextSizeValue
> = { xs: "xs", sm: "xs", md: "sm", lg: "sm" };

const itemSizeToContainerPadding: Record<
  RedoListItemSize,
  { px: SpacingValue; py: SpacingValue }
> = {
  xs: { px: "xs", py: "xs" },
  sm: { px: "xs", py: "xs" },
  md: { px: "md", py: "xs" },
  lg: { px: "md", py: "md" },
};
